
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { DebouncedFunc, debounce, isEqual } from 'lodash';
import { PaginatedParams } from 'ah-api-gateways';

/**
 * Base List block component
 *
 * Meant for usage alongside a <Listing> and a <fooFilter> components, controlling filter flow between both
 *
 * update-filter:  used for one-way syncing to an external value
 */
@Component
export default class ListBlock extends Vue {
  /**
   * Possible set common filters of the list
   *
   * This filters will remain true despite the filters
   * set by the user
   */
  @Prop({ required: false }) commonFilters?: any;

  /**
   * Possible set initial filters of the list
   *
   * This filters can be overwritten by the user
   */
  @Prop({ required: false }) defaultFilters?: any;

  /**
   * Possible set common sort and page params of the list
   *
   * This params will remain true despite the sort and page
   * inputed by the user
   */
  @Prop({ required: false }) commonSortAndPageParams?: Partial<PaginatedParams>;

  /**
   * Possible set common sort and page params of the list
   *
   * This sort and page params can be overwritten by the user
   */
  @Prop({ required: false }) defaultSortAndPageParams?: Partial<PaginatedParams>;

  filter: any = {};

  sortAndPageParams: Partial<PaginatedParams> = {};

  debouncedFilter!: DebouncedFunc<(filters: any) => void>;

  debouncedSortAndPageParams!: DebouncedFunc<(sortAndPageParams: any) => void>;

  private blockDebounce = true;

  mounted() {
    setTimeout(() => (this.blockDebounce = false));
    this.debouncedFilter = debounce((filter: any) => this.setFilter(filter), 800);
    this.debouncedSortAndPageParams = debounce(
      (sortAndPageParams: any) => this.setSortAndPageParams(sortAndPageParams),
      800
    );
  }

  setFilter(filter: any) {
    const newFilter = { ...filter, ...this.commonFilters };
    if (!isEqual(newFilter, this.filter)) {
      this.filter = newFilter;
      this.$emit('update:filter', this.filter);
    }
  }

  addToFilter(filter: any) {
    const newFilter = { ...this.filter, ...filter, ...this.commonFilters };
    if (!isEqual(newFilter, this.filter)) {
      this.filter = newFilter;
      this.$emit('update:filter', this.filter);
    }
  }

  setSortAndPageParams(sortAndPageParams: any) {
    if (!isEqual(sortAndPageParams, this.sortAndPageParams)) {
      this.sortAndPageParams = { ...sortAndPageParams, ...this.commonSortAndPageParams };
    }
  }

  onFilterChange(filter: any) {
    if (this.blockDebounce) {
      this.setFilter(filter);
    } else {
      this.debouncedFilter(filter);
    }
  }

  onSortAndPageParamsChange(sortAndPageParams: any) {
    if (this.blockDebounce) {
      this.setSortAndPageParams(sortAndPageParams);
    } else {
      this.debouncedSortAndPageParams(sortAndPageParams);
    }
  }

  beforeDestroy() {
    this.debouncedFilter.cancel();
    this.debouncedSortAndPageParams.cancel();
  }

  @Watch('defaultSortAndPageParams', { immediate: true })
  onDefaultSortChange() {
    if (this.defaultSortAndPageParams) {
      this.sortAndPageParams = this.defaultSortAndPageParams;
    }
  }

  @Watch('defaultFilters', { immediate: true })
  onDefaultFiltersChange() {
    if (this.defaultFilters) {
      this.setFilter(this.defaultFilters);
    }
  }

  @Watch('commonFilters', { immediate: true, deep: true })
  onCommonFiltersChange() {
    this.setFilter({ ...this.filter, ...this.commonFilters });
  }
}
